import { useCallback, useMemo } from 'react';
import {
  getEmptyStateCopy,
  prepareTimeSeriesChart,
  resolveEmptyVariant,
} from '../../utils/dashboardEmptyState';
import {
  REQUESTS_TIME_SERIES,
  TOKEN_TIME_SERIES,
  CLOUD_SPEND_TIME_SERIES,
} from '../../utils/chartTimeSeries';
import { resolveChartGranularity } from '../../utils/timeRange';

export function useDashboardCharts({
  timeRange,
  statistics,
  recentActivityTotal,
  requestsData,
  tokenUsageData,
  cloudSpendData,
  assistantUsageData,
  modelLatencyData,
  onTimeRangeChange,
}) {
  const buildChartEmptyState = useCallback((chartKind, variant) => {
    if (!variant) return null;
    const copy = getEmptyStateCopy({ variant, chartKind, timeRange });
    return {
      title: copy.title,
      description: copy.description,
      tone: copy.tone,
      actionLabel: copy.actionLabel,
      onAction: copy.actionPeriod ? () => onTimeRangeChange({ period: copy.actionPeriod }) : undefined,
    };
  }, [timeRange, onTimeRangeChange]);

  const toLocalChartTime = useCallback((time) => {
    const granularity = resolveChartGranularity(timeRange);
    const usesTimeBuckets = granularity === 'hour' || granularity === 'five_minute';

    if (usesTimeBuckets && /^\d{2}:\d{2}$/.test(time)) {
      const [hour, minute = '00'] = time.split(':');
      const now = new Date();
      const utcDate = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        parseInt(hour, 10),
        parseInt(minute, 10),
        0,
        0,
      ));
      if (timeRange?.period === 'yesterday') {
        utcDate.setUTCDate(utcDate.getUTCDate() - 1);
      }
      const localHour = utcDate.getHours().toString().padStart(2, '0');
      const localMinute = utcDate.getMinutes().toString().padStart(2, '0');
      return `${localHour}:${localMinute}`;
    }
    return time;
  }, [timeRange]);

  const mapRequestsItem = useCallback((item) => ({
    time: toLocalChartTime(item.time),
    api_successful: item.api_successful || 0,
    api_failed: item.api_failed || 0,
    direct_successful: item.direct_successful || 0,
    direct_failed: item.direct_failed || 0,
    bot_successful: item.bot_successful || 0,
    bot_failed: item.bot_failed || 0,
    widget_successful: item.widget_successful || 0,
    widget_failed: item.widget_failed || 0,
    successful: item.successful
      || (item.api_successful || 0) + (item.direct_successful || 0)
      + (item.bot_successful || 0) + (item.widget_successful || 0),
    failed: item.failed
      || (item.api_failed || 0) + (item.direct_failed || 0)
      + (item.bot_failed || 0) + (item.widget_failed || 0),
    direct_requests: item.direct_requests || (item.direct_successful || 0) + (item.direct_failed || 0),
  }), [toLocalChartTime]);

  const mapTokenItem = useCallback((item) => ({
    time: toLocalChartTime(item.time),
    platform_tokens: item.platform_tokens || 0,
    api_tokens: item.api_tokens || 0,
    bot_tokens: item.bot_tokens || 0,
    widget_tokens: item.widget_tokens || 0,
    tokens: item.tokens ?? (
      (item.platform_tokens || 0)
      + (item.api_tokens || 0)
      + (item.bot_tokens || 0)
      + (item.widget_tokens || 0)
    ),
  }), [toLocalChartTime]);

  const mapCloudSpendItem = useCallback((item) => ({
    time: toLocalChartTime(item.time),
    platform_cost_usd: Number(item.platform_cost_usd) || 0,
    api_cost_usd: Number(item.api_cost_usd) || 0,
    bot_cost_usd: Number(item.bot_cost_usd) || 0,
    widget_cost_usd: Number(item.widget_cost_usd) || 0,
    total_cost_usd: item.total_cost_usd ?? (
      (Number(item.platform_cost_usd) || 0)
      + (Number(item.api_cost_usd) || 0)
      + (Number(item.bot_cost_usd) || 0)
      + (Number(item.widget_cost_usd) || 0)
    ),
  }), [toLocalChartTime]);

  const mapFailuresItem = useCallback((item) => ({
    time: toLocalChartTime(item.time),
    failed: (item.api_failed || 0) + (item.direct_failed || 0)
      + (item.bot_failed || 0) + (item.widget_failed || 0)
      + (item.failed || 0),
  }), [toLocalChartTime]);

  const requestsChart = useMemo(() => {
    const prepared = prepareTimeSeriesChart({
      rawData: requestsData,
      timeRange,
      statistics,
      recentActivityTotal,
      timeSeries: REQUESTS_TIME_SERIES,
    });
    return {
      data: prepared.data.map(mapRequestsItem),
      emptyState: prepared.emptyState
        ? buildChartEmptyState('requests', prepared.emptyState.variant)
        : null,
    };
  }, [requestsData, timeRange, statistics, recentActivityTotal, mapRequestsItem, buildChartEmptyState]);

  const tokenChart = useMemo(() => {
    const prepared = prepareTimeSeriesChart({
      rawData: tokenUsageData,
      timeRange,
      statistics,
      recentActivityTotal,
      timeSeries: TOKEN_TIME_SERIES,
    });
    return {
      data: prepared.data.map(mapTokenItem),
      emptyState: prepared.emptyState
        ? buildChartEmptyState('tokens', prepared.emptyState.variant)
        : null,
    };
  }, [tokenUsageData, timeRange, statistics, recentActivityTotal, mapTokenItem, buildChartEmptyState]);

  const cloudSpendChart = useMemo(() => {
    const prepared = prepareTimeSeriesChart({
      rawData: cloudSpendData,
      timeRange,
      statistics,
      recentActivityTotal,
      timeSeries: CLOUD_SPEND_TIME_SERIES,
    });
    return {
      data: prepared.data.map(mapCloudSpendItem),
      emptyState: prepared.emptyState
        ? buildChartEmptyState('cloudSpend', prepared.emptyState.variant)
        : null,
    };
  }, [cloudSpendData, timeRange, statistics, recentActivityTotal, mapCloudSpendItem, buildChartEmptyState]);

  const failuresChart = useMemo(() => {
    const prepared = prepareTimeSeriesChart({
      rawData: requestsData,
      timeRange,
      statistics,
      recentActivityTotal,
      timeSeries: REQUESTS_TIME_SERIES,
    });
    return {
      data: prepared.data.map(mapFailuresItem),
      emptyState: prepared.emptyState
        ? buildChartEmptyState('requests', prepared.emptyState.variant)
        : null,
    };
  }, [requestsData, timeRange, statistics, recentActivityTotal, mapFailuresItem, buildChartEmptyState]);

  const pieChartEmptyState = useMemo(() => {
    if (assistantUsageData.length > 0) return null;
    const variant = resolveEmptyVariant({ statistics, recentActivityTotal });
    return buildChartEmptyState('pie', variant);
  }, [assistantUsageData, statistics, recentActivityTotal, buildChartEmptyState]);

  const latencyChartEmptyState = useMemo(() => {
    if (modelLatencyData.length > 0) return null;
    const variant = resolveEmptyVariant({ statistics, recentActivityTotal });
    return buildChartEmptyState('latency', variant);
  }, [modelLatencyData, statistics, recentActivityTotal, buildChartEmptyState]);

  const errorRate = useMemo(() => computeErrorRateFromRaw(requestsData), [requestsData]);

  return {
    requestsChart,
    tokenChart,
    cloudSpendChart,
    failuresChart,
    pieChartEmptyState,
    latencyChartEmptyState,
    errorRate,
  };
}

function computeErrorRateFromRaw(requestsData) {
  if (!requestsData?.length) return null;
  let total = 0;
  let failed = 0;
  requestsData.forEach((item) => {
    const success = (item.api_successful || 0) + (item.direct_successful || 0)
      + (item.bot_successful || 0) + (item.widget_successful || 0);
    const fail = (item.api_failed || 0) + (item.direct_failed || 0)
      + (item.bot_failed || 0) + (item.widget_failed || 0);
    total += success + fail;
    failed += fail;
  });
  if (total === 0) return 0;
  return (failed / total) * 100;
}
